import { useTranslation } from "next-i18next";
import { m, animate, stagger, useInView } from "framer-motion";
import { useMediaQuery } from "usehooks-ts";
import { useRef, useState, useEffect } from "react";

const HistorySection = () => {
  const inViewRef = useRef(null);
  const inView = useInView(inViewRef, { once: true });

  const animation = [
    [".bullet", { opacity: 1 }, { duration: 0.3, delay: stagger(1) }],
    [
      ".marker",
      { opacity: 1, x: 0 },
      { duration: 0.7, delay: stagger(1), at: 0.3 },
    ],
    [
      ".history-text",
      { opacity: 1, x: 0 },
      { duration: 0.7, delay: stagger(1), at: 0.3 },
    ],
    [
      ".v-bar",
      { height: "8rem" },
      { duration: 0.5, delay: stagger(1), at: 0.5 },
    ],
  ];

  useEffect(() => {
    if (inView) {
      // console.log("in view");
      animate(animation);
    }
  }, [inView]);

  const data = [
    {
      year: "2009",
      text: "AlHayat Foodstuff is established in Qatar with an initial investment of over 1 million USD and three employees.",
    },
    {
      year: "2015",
      text: "Expansion to Egypt and Poland marks a strategic move towards global presence.",
    },
    {
      year: "2017",
      text: "A pivotal year sees the establishment of a cutting-edge factory spanning 18,500 sqm in Poland. This facility becomes a cornerstone for storing and packing premium frozen vegetables.",
    },
    {
      year: "2022",
      text: "AlHayat Foodstuff transitions from local distribution to focus on mass production of premium frozen vegetables, directly sourcing from farmers.",
    },
    {
      year: "2023",
      text: "Inauguration of another state-of-the-art factory in Qatar signifies our commitment to growth and innovation.",
    },
    {
      year: "Present",
      text: "AlHayat Foodstuff stands as a global leader, delivering superior frozen vegetables to distributors worldwide.",
    },
  ];

  return (
    <section className="flex bg-auto-black/5">
      <div className="container flex flex-col items-start justify-start gap-10 px-4 py-16 md:flex-row">
        <m.div
          className="flex flex-col items-start justify-start gap-5 self-stretch md:w-[60rem]"
          initial={{ opacity: 0 }}
          whileInView={{
            opacity: 1,
            transition: { duration: 0.5, ease: "easeOut" },
          }}
          viewport={{ once: true }}
        >
          <h2
            id="history"
            className="self-stretch p-0 text-3xl font-bold leading-9 text-primary-700"
          >
            The Evolution of AlHayat Foodstuff
          </h2>
          <div
            className="self-stretch text-base font-medium leading-normal text-auto-black/60"
            ref={inViewRef}
          >
            Founded in 2009 in Qatar, AlHayat Foodstuff embarked on a journey
            driven by a commitment to providing the Qatari market with the
            finest food products. Over the years, our dedication to
            transparency, sustainability, and excellence has propelled us beyond
            borders, transforming our story into a global legacy.
          </div>
        </m.div>
        <div className="grid grid-cols-[2rem_auto] gap-x-4 sm:grid-cols-[5rem_2rem_auto]">
          <div className="order-1 row-span-12 flex w-2 flex-col items-center justify-start justify-self-center sm:order-2">
            {data.map((_, index) => (
              <m.div
                className={`v-bar relative flex h-32 w-2 translate-y-1 flex-col items-center justify-start ${index < data.length - 1 ? "bg-neutral-700 dark-theme:bg-neutral-300" : "bg-transparent"}`}
                key={index}
                initial={{ height: 0 }}
              >
                <m.div
                  className="bullet absolute top-0 h-6 w-6 -translate-y-1 rounded-full bg-neutral-700 p-1.5 dark-theme:bg-neutral-300"
                  initial={{ opacity: 0 }}
                />
              </m.div>
            ))}
          </div>
          {data.map((item, index) => (
            <>
              <Marker year={item.year} i={index} />
              <HistoryText text={item.text} />
            </>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HistorySection;

const Marker = ({ year, i }) => {
  const { i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  const [key, setKey] = useState(0);

  const isXs = useMediaQuery("(max-width: 600px)", {
    initializeWithValue: false,
  });

  useEffect(() => {
    setKey(isXs ? 1 : 3);
  }, [isXs]);

  return (
    <m.div
      key={key}
      className={`marker flex h-6 flex-col items-start justify-center gap-2 sm:items-end ${i === 0 ? "order-2 sm:order-1" : "order-3"}`}
      initial={{ opacity: 0, x: rtl ? (isXs ? -50 : 50) : isXs ? 50 : -50 }}
    >
      <div className="relative flex h-6 items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-1 text-xs font-semibold leading-[18px] text-white/85 sm:h-10 sm:rounded-lg sm:py-2 sm:text-base">
        {year}
        <svg
          viewBox="0 0 11 12"
          className={`absolute ${rtl ? "-left-3 -scale-100" : "-right-3"} top-3 hidden h-4 w-4 fill-primary-500 sm:block`}
          preserveAspectRatio="none"
        >
          <path d="M11 6L-5.24537e-07 12L0 -4.80825e-07L11 6Z" />
        </svg>
      </div>
    </m.div>
  );
};

const HistoryText = ({ text }) => {
  const { i18n } = useTranslation();
  const rtl = i18n.dir() === "rtl";
  return (
    <m.div
      className="history-text order-3 mb-4 mt-2 h-20 self-stretch text-sm font-medium leading-normal text-auto-black/60 sm:mb-4 sm:mt-0 sm:h-28 sm:text-base"
      initial={{ opacity: 0, x: rtl ? -50 : 50 }}
    >
      {text}
    </m.div>
  );
};
